import { useEffect, useState } from 'react';
import {
  apiCancelOrder,
  apiFulfilOrder,
  apiGetOrder,
  apiRefundOrder,
  type Order,
  type OrderDetail,
} from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { AdminActionsMenu, AdminExtensionSlot } from '~/components/workbench/admin-extensions';
import {
  addressLines,
  apiErrorMessage,
  formatDate,
  formatMinor,
  isHttpUrl,
  minorToMajorInput,
  orderActions,
  orderStatusMeta,
  orderTaxExclusive,
  parseMoney,
} from './shop-model';

/** Order detail, drawn over the Shop panel. Changes reload the drawer and tell the list. */
export function OrderDrawer({
  appId,
  orderId,
  onClose,
  onChanged,
}: {
  appId: string;
  orderId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = useResource(() => apiGetOrder(appId, orderId), `${appId}:${orderId}`);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const changed = (next?: OrderDetail) => {
    if (next) {
      detail.setData(next);
    } else {
      void detail.reload();
    }

    onChanged();
  };

  const order = detail.data?.order;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-surface" role="dialog" aria-label="Order details">
      <div className="flex items-center justify-between gap-2 border-b border-hairline px-4 py-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="ui-btn-icon" aria-label="Back to orders">
            <div className="i-ph:arrow-left text-[14px]" />
          </button>
          <h3 className="font-geist-mono text-[13px] text-primary">{order ? `Order #${order.number}` : 'Order'}</h3>
          {order && (
            <span className="text-[10px] uppercase tracking-[0.08em] text-muted">
              {order.mode === 'test' ? 'Test' : 'Live'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <AdminActionsMenu appId={appId} target="shop.order.actions" resourceId={orderId} compact />
          {order && <Pill tone={orderStatusMeta(order.status).tone} label={orderStatusMeta(order.status).label} />}
        </div>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        {detail.data ? (
          <>
            <OrderBody appId={appId} detail={detail.data} onChanged={changed} />
            <AdminExtensionSlot appId={appId} target="shop.order.details" resourceId={orderId} />
          </>
        ) : detail.error ? (
          <LoadError message="Could not load this order." onRetry={() => void detail.reload()} />
        ) : (
          <>
            <SkeletonCard lines={3} />
            <SkeletonCard lines={2} />
          </>
        )}
      </div>
    </div>
  );
}

function OrderBody({
  appId,
  detail,
  onChanged,
}: {
  appId: string;
  detail: OrderDetail;
  onChanged: (next?: OrderDetail) => void;
}) {
  const { order, items } = detail;
  const actions = orderActions(order);
  const status = orderStatusMeta(order.status);
  const address = addressLines(order.shippingAddress);
  const money = (n: number) => formatMinor(n, order.currency);

  return (
    <>
      {status.attention && (
        <div className="ui-banner-error items-start leading-relaxed">
          <div className="i-ph:warning-circle mt-px shrink-0 text-sm text-danger" />
          <span className="text-[12px] text-danger/90">
            {order.status === 'needs_refund'
              ? 'This order was paid after its stock ran out. Refund the buyer.'
              : 'The buyer disputed this payment with their bank. Respond from your payment provider.'}
          </span>
        </div>
      )}

      <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
        {items.map((item) => (
          <div key={item.id} className="flex items-start justify-between gap-3 px-4 py-2.5">
            <div className="min-w-0">
              <div className="truncate text-[13px] text-primary">{item.title}</div>
              <div className="flex flex-wrap gap-x-1.5 text-[11px] text-muted">
                {item.variantTitle && <span>{item.variantTitle}</span>}
                <span className="font-geist-mono text-[10px]">{item.sku}</span>
              </div>
            </div>
            <div className="shrink-0 text-right font-geist-mono text-[12px]">
              <div className="text-primary">{money(item.unitPrice * item.quantity)}</div>
              {item.quantity > 1 && (
                <div className="text-[10px] text-muted">
                  {item.quantity} × {money(item.unitPrice)}
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="space-y-1 px-4 py-3 text-[12px]">
          <TotalRow label="Subtotal" value={money(order.subtotal)} />
          {order.discount > 0 && (
            <TotalRow
              label={order.discountCode ? `Discount (${order.discountCode})` : 'Discount'}
              value={`−${money(order.discount)}`}
            />
          )}
          <TotalRow label="Shipping" value={order.shipping ? money(order.shipping) : 'Free'} />
          {order.tax > 0 && (
            <TotalRow
              label={orderTaxExclusive(order) ? 'Tax' : 'Tax (included in prices)'}
              value={orderTaxExclusive(order) ? money(order.tax) : `(${money(order.tax)})`}
              muted={!orderTaxExclusive(order)}
            />
          )}
          <div className="mt-1.5 border-t border-hairline pt-1.5">
            <TotalRow label="Total" value={money(order.total)} strong />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <div className={CAPTION}>Buyer</div>
          <div className="text-[12px] text-primary">{order.buyerName || '—'}</div>
          {order.buyerEmail && <div className="break-all text-[12px] text-secondary">{order.buyerEmail}</div>}
          <div className="text-[11px] text-muted">Placed {formatDate(order.createdAt)}</div>
          {order.paidAt && <div className="text-[11px] text-muted">Paid {formatDate(order.paidAt)}</div>}
        </div>
        <div className="space-y-1">
          <div className={CAPTION}>Ship to</div>
          {address.length ? (
            address.map((line, i) => (
              <div key={i} className="text-[12px] text-secondary">
                {line}
              </div>
            ))
          ) : (
            <div className="text-[12px] text-muted">No address</div>
          )}
        </div>
      </div>

      {(order.trackingNumber || order.trackingUrl || order.fulfilledAt) && (
        <div className="space-y-1">
          <div className={CAPTION}>Fulfilment</div>
          {order.fulfilledAt && <div className="text-[11px] text-muted">Fulfilled {formatDate(order.fulfilledAt)}</div>}
          {order.trackingNumber && (
            <div className="font-geist-mono text-[12px] text-secondary">{order.trackingNumber}</div>
          )}
          {order.trackingUrl && (
            <a
              href={order.trackingUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="break-all text-[12px] text-info hover:underline"
            >
              {order.trackingUrl}
            </a>
          )}
        </div>
      )}

      {order.note && (
        <div className="space-y-1">
          <div className={CAPTION}>Note</div>
          <p className="text-[12px] leading-relaxed text-secondary">{order.note}</p>
        </div>
      )}

      {(actions.fulfil || actions.refund || actions.cancel) && (
        <div className="space-y-2">
          {actions.fulfil && <FulfilForm appId={appId} order={order} onDone={() => onChanged()} />}
          {actions.refund && <RefundForm appId={appId} order={order} onDone={onChanged} />}
          {actions.cancel && <CancelButton appId={appId} order={order} onDone={() => onChanged()} />}
        </div>
      )}
    </>
  );
}

function TotalRow({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={strong ? 'text-primary' : 'text-muted'}>{label}</span>
      <span
        className={classNames(
          'font-geist-mono',
          strong ? 'text-[13px] text-primary' : muted ? 'text-muted' : 'text-secondary',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function FulfilForm({ appId, order, onDone }: { appId: string; order: Order; onDone: () => void }) {
  const isUpdate = order.status === 'fulfilled';
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState(order.trackingNumber ?? '');
  const [url, setUrl] = useState(order.trackingUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (url.trim() && !isHttpUrl(url)) {
      setError('The tracking link must start with https://');
      return;
    }

    setBusy(true);

    try {
      await apiFulfilOrder(appId, order.id, {
        trackingNumber: number.trim() || null,
        trackingUrl: url.trim() || null,
      });
      setOpen(false);
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not mark this order fulfilled.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={BTN_SECONDARY}>
        <div className="i-ph:truck text-[13px]" />
        {isUpdate ? 'Update tracking' : 'Mark fulfilled'}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field label="Tracking number" hint="Optional.">
        <input value={number} onChange={(e) => setNumber(e.target.value)} className="ui-input-mono !py-2" />
      </Field>
      <Field label="Tracking link" hint="Optional. Shown to the buyer on their order page.">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
          spellCheck={false}
          className="ui-input-mono !py-2"
        />
      </Field>
      {error && <ErrorBanner message={error} />}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          {isUpdate ? 'Save tracking' : 'Mark fulfilled'}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RefundForm({ appId, order, onDone }: { appId: string; order: Order; onDone: (next: OrderDetail) => void }) {
  // The order doesn't carry the refunded amount, so a partly refunded order
  // defaults to "whatever is left" (amount omitted) instead of a number.
  const knownFull = order.status !== 'partially_refunded';
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setAmount(knownFull ? minorToMajorInput(order.total, order.currency) : '');
    setError(null);
    setOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    let minor: number | undefined;

    if (amount.trim()) {
      const parsed = parseMoney(amount, order.currency);

      if (parsed === null || parsed > order.total) {
        setError(
          `Enter an amount up to ${formatMinor(order.total, order.currency)}, or leave it blank for a full refund.`,
        );
        return;
      }

      // Omit the amount for a full refund so the server refunds exactly what's left.
      minor = knownFull && parsed === order.total ? undefined : parsed;
    }

    setBusy(true);

    try {
      onDone(await apiRefundOrder(appId, order.id, minor));
      setOpen(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not refund this order.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={start} className={BTN_SECONDARY}>
        <div className="i-ph:arrow-u-up-left text-[13px]" />
        Refund
      </button>
    );
  }

  return (
    <form onSubmit={submit} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field
        label={`Refund amount (${order.currency})`}
        hint={
          knownFull
            ? 'Defaults to the full order total. The money goes back through the payment that paid it.'
            : 'Leave blank to refund everything that is left.'
        }
      >
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder={knownFull ? undefined : 'Full remaining'}
          className="ui-input-mono !w-36 !py-2"
        />
      </Field>
      {error && <ErrorBanner message={error} />}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          Refund
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CancelButton({ appId, order, onDone }: { appId: string; order: Order; onDone: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async () => {
    setBusy(true);
    setError(null);

    try {
      await apiCancelOrder(appId, order.id);
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not cancel this order.'));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-secondary">Cancel this unpaid order and release its stock?</span>
          <button type="button" onClick={() => void cancel()} disabled={busy} className={BTN_SECONDARY}>
            {busy && <Spinner />}
            Cancel order
          </button>
          <button type="button" onClick={() => setConfirming(false)} disabled={busy} className={BTN_GHOST}>
            Keep it
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirming(true)} className={BTN_GHOST}>
          <div className="i-ph:x-circle text-[13px]" />
          Cancel order
        </button>
      )}
      {error && <ErrorBanner message={error} />}
    </div>
  );
}
